import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { hasFreeAnonymousHints, getHintAuthMessage } from '../utils/anonymousHints';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { buildTrackedAuthRedirectUrl, logGameEvent, trackGameHintInsufficientBalance } from "../analytics";
import { getGameConfig, getGameMessage } from "../games";
import { GameShell, useAiutiniModal } from './layout';
import { GameEndSection } from './game';
import { ScalinataSkeleton } from './Skeleton';
import SnackAlert from './SnackAlert.js';
import { hasGameHistory } from "../util";
import styles from "../styles/Scalinata.module.css";
import globalStyles from "../styles/global.module.css";
import modalStyles from "../styles/modal.module.css";
import gameStyles from "../styles/GameStyles.module.css";
import Button from './Button';
import AdBanner from './ads/AdBanner';
import AIHelperTrigger from './ai-helper/AIHelperTrigger';
import { useGettoncini } from "../contexts/GettonciniContext";
import { usePuzzleInstances } from '../contexts/PuzzleInstancesContext';
import useHasEverAuthenticated from "../hooks/useHasEverAuthenticated";
import { useScalinataEvents } from "../hooks/games/useScalinataEvents";
import GettonciniCostChip from './GettonciniCostChip';
import RewardedGettonciniCard from './RewardedGettonciniCard';
import {
  canAffordHintCost,
  getScalinataHintErrorMessage,
  INSUFFICIENT_HINT_BALANCE_MESSAGE,
} from './scalinataHintUi';
import {
  getScalinataFooterAdHeight,
  getScalinataFooterBannerSize,
} from './scalinataFooterAd';
import FaIcon from './FaIcon';
import { getScalinataMaxGuesses, getScalinataWordLengths } from '../gameVariants';
import { getDisabledHintCount, getDisabledHintsMessage } from '../utils/aiutiniModal';

const getViewportWidth = () => {
  if (typeof window === 'undefined') return 0;
  return window.innerWidth || document.documentElement.clientWidth || 0;
};

const ScalinataLevel = ({ level, isActive, numberOfLetters, guesses, currentGuess, gameOver, answer, showAnswer, animationDelay, magicLetters, isSubmitting, showAllGuesses }) => {
  let wordsDiv = null;
  if (!showAnswer) {
    wordsDiv = guesses.map(({ guess, feedback }, index) => {
      const shouldFade = !showAllGuesses && !isActive && index < guesses.length - 1;
      const shouldHide = !showAllGuesses && gameOver && index < guesses.length - 1;
      const isDone = !isActive && index === guesses.length - 1;
      const isSelectable = gameOver && isDone;
      return (
        <div
          key={index}
          className={styles.guessRow + (shouldFade ? ' ' + styles.fadeOut : '') + (shouldHide ? ' ' + styles.hidden : '') + (isSelectable ? ' ' + styles.answerSelectable : '')}
          {...(isSelectable ? { 'data-ai-component': 'answer_word', 'data-ai-word': guess.toLowerCase() } : {})}
        >
          {Array.from({ length: numberOfLetters }).map((_, i) => (
            <div
              key={i}
              className={[
                styles.letterBox,
                isDone && styles.shimmer,
                feedback[i]?.state && styles[feedback[i].state]
              ].filter(Boolean).join(' ')}
            >
              {guess.toUpperCase()[i] || ""}
            </div>
          ))}
        </div>
      );
    });
  } else {
    if (guesses.length > 0) {
      wordsDiv = guesses.map(({ guess, feedback }, index) => {
        const shouldFade = true;
        const isDone = false;
        return (
          <div key={index} className={styles.guessRow + (shouldFade ? ' ' + styles.fadeOut : '')}>
            {Array.from({ length: numberOfLetters }).map((_, i) => (
              <div
                key={i}
                className={[
                  styles.letterBox,
                  isDone && styles.shimmer,
                  feedback[i]?.state && styles[feedback[i].state]
                ].filter(Boolean).join(' ')}
              >
                {guess.toUpperCase()[i] || ""}
              </div>
            ))}
          </div>
        );
      });
      if (answer) {
        wordsDiv.push((
          <div key={guesses.length} className={styles.guessRow + ' ' + styles.answer + ' ' + styles.answerSelectable} style={{ animationDelay: `${animationDelay}s` }} data-ai-component="answer_word" data-ai-word={answer.toLowerCase()}>
            {Array.from({ length: numberOfLetters }).map((_, i) => (
              <div
                key={i}
                className={[
                  styles.letterBox,
                  styles.correct
                ].join(' ')}
              >
                {answer.toUpperCase()[i] || ""}
              </div>
            ))}
          </div>
        ));
      }
    } else if (answer) {
      wordsDiv = (
        <div className={styles.guessRow + ' ' + styles.answer + ' ' + styles.answerSelectable} style={{ animationDelay: `${animationDelay}s` }} data-ai-component="answer_word" data-ai-word={answer.toLowerCase()}>
          {Array.from({ length: numberOfLetters }).map((_, i) => (
            <div
              key={i}
              className={[
                styles.letterBox,
                styles.correct
              ].join(' ')}
            >
              {answer.toUpperCase()[i] || ""}
            </div>
          ))}
        </div>
      );
    }
  }
  return (
    <div className={styles.scalinataLevel}>
      <div className={styles.guesses}>
        {wordsDiv}
        {isActive && !gameOver && (
          <div className={[
            styles.guessRow,
            styles.currentGuess,
            isSubmitting && styles.submittingRow
          ].filter(Boolean).join(' ')}>
            {Array.from({ length: numberOfLetters }).map((_, index) => {
              const userLetter = currentGuess.toUpperCase()[index] || "";
              const magicLetter = magicLetters && magicLetters[index] ? magicLetters[index].toUpperCase() : "";
              const displayLetter = userLetter || magicLetter;
              const isMagicHint = !userLetter && magicLetter;
              
              return (
                <div
                  key={index}
                  className={[
                    styles.letterBox,
                    !displayLetter && !isSubmitting && styles.shimmer,
                    isMagicHint && styles.magicHint,
                    isSubmitting && styles.submittingLetter
                  ].filter(Boolean).join(' ')}
                >
                  {displayLetter}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const Keyboard = ({ keyboardState, handleLetterClick, handleDelete, handleSubmit, isSubmitting }) => {
  return (
    <div className={styles.keyboard}>
      <div className={styles.keyboardRow}>
        {"qwertyuiop".split("").map((letter) => (
          <button
            key={letter}
            className={[
              styles.key,
              keyboardState[letter] && styles[keyboardState[letter]]
            ].filter(Boolean).join(' ')}
            onClick={() => handleLetterClick(letter)}
            disabled={isSubmitting}
          >
            {letter.toUpperCase()}
          </button>
        ))}
      </div>
      <div className={styles.keyboardRow}>
        {"asdfghjkl".split("").map((letter) => (
          <button
            key={letter}
            className={[
              styles.key,
              keyboardState[letter] && styles[keyboardState[letter]]
            ].filter(Boolean).join(' ')}
            onClick={() => handleLetterClick(letter)}
            disabled={isSubmitting}
          >
            {letter.toUpperCase()}
          </button>
        ))}
      </div>
      <div className={styles.keyboardRow}>
        <button 
          className={[
            styles.key,
            styles.special,
            isSubmitting && styles.submitting
          ].filter(Boolean).join(' ')} 
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? <FaIcon className="fas fa-spinner fa-spin"  /> : 'INVIA'}
        </button>
        {"zxcvbnm".split("").map((letter) => (
          <button
            key={letter}
            className={[
              styles.key,
              keyboardState[letter] && styles[keyboardState[letter]]
            ].filter(Boolean).join(' ')}
            onClick={() => handleLetterClick(letter)}
            disabled={isSubmitting}
          >
            {letter.toUpperCase()}
          </button>
        ))}
        <button 
          className={[
            styles.key,
            styles.special
          ].join(' ')} 
          onClick={handleDelete}
          disabled={isSubmitting}
        >
          <FaIcon className="fas fa-backspace"  />
        </button>
      </div>
    </div>
  );
};

const ScalinataGame = ({
  date,
  gameConfig: providedGameConfig,
  puzzleConfig,
  sessionState,
  solutions: hookSolutions,
  gameOver,
  won,
  justWon,
  justLost = false,
  isLoading,
  sessionError,
  isEventPending,
  dispatchEvent,
  dispatchHintEvent,
  hintAllowance = null,
  refreshHintAllowance = null,
  isSignedIn = false,
  isPreview = false,
  previewSponsorship = null,
  showNavigation = true,
  showGameHeader = true,
  previewEndLabel,
}) => {
  const hasFreeHints = hasFreeAnonymousHints(hintAllowance, 'scalinata', isSignedIn);
  const navigate = useNavigate();
  const location = useLocation();
  const hasEverAuthenticated = useHasEverAuthenticated();
  const gameConfig = providedGameConfig || getGameConfig('scalinata');
  const effectiveDate = date || 'preview';
  const shouldTrack = !isPreview;
  
  const { balance, getHintCost } = useGettoncini();
  const { getPuzzleInstanceId, getPuzzleInstance } = usePuzzleInstances();
  const puzzleInstanceId = getPuzzleInstanceId(gameConfig.id, effectiveDate);
  const sponsorship = previewSponsorship || (isPreview ? null : getPuzzleInstance(gameConfig.id, effectiveDate)?.sponsorship || null);
  const {
    isModalOpen,
    modalTitle,
    modalContent,
    closeModal,
    showModal,
    showAiutiniModal,
    refreshAiutiniModal,
    isAiutiniModalOpen,
  } = useAiutiniModal();

  useEffect(() => {
    if (isAiutiniModalOpen) refreshHintAllowance?.();
  }, [isAiutiniModalOpen, refreshHintAllowance]);

  
  const { submitWord, requestDefinition, requestClue, requestMagic, requestRevealTheme } = useScalinataEvents(dispatchEvent, dispatchHintEvent);
  const [currentGuess, setCurrentGuess] = useState("");
  const [showEndContent, setShowEndContent] = useState(false);
  const [loadingHint, setLoadingHint] = useState(null);
  const [aiutiniError, setAiutiniError] = useState("");
  const [endSectionAnchor, setEndSectionAnchor] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmitPending = isSubmitting || (isEventPending && loadingHint === null);
  const [revealedAnswers, setRevealedAnswers] = useState(null);
  const [showAllGuesses, setShowAllGuesses] = useState(false);

  const wordLengths = getScalinataWordLengths(puzzleConfig);
  const maxGuesses = getScalinataMaxGuesses(puzzleConfig);
  const levels = wordLengths.map((_, idx) => idx);
  const allGuesses = sessionState?.guesses || {};
  const keyboardState = sessionState?.keyboard_state || {};
  const currentLevel = sessionState?.current_level ?? 0;
  const currentWordLength = wordLengths[currentLevel] || wordLengths[wordLengths.length - 1] || 0;
  const lives = Math.max(0, Math.min(sessionState?.lives ?? maxGuesses, maxGuesses));
  const remainingGuesses = lives;
  const theme = puzzleConfig?.theme || null;
  
  const solutionWords = hookSolutions?.[0]?.words || [];
  const answers = revealedAnswers || (solutionWords.length > 0 ? solutionWords : Array(levels.length).fill(null));
  
  const hintsUsed = sessionState?.hints_used || { reveal_theme: false };
  const revealThemeUsed = hintsUsed.reveal_theme || false;
  const revealedTheme = sessionState?.revealed_theme || null;
  const clue = sessionState?.clue || null;
  const definition = sessionState?.definition || null;
  const magicLetters = sessionState?.magic_letters || null;
  const footerBannerSize = getScalinataFooterBannerSize({
    enabled: !gameOver,
    viewportWidth: getViewportWidth(),
  });
  const footerAdHeight = getScalinataFooterAdHeight(footerBannerSize);
  const mobileGameplayAd = useMemo(() => ({
    enabled: !!endSectionAnchor,
    anchors: endSectionAnchor ? [endSectionAnchor] : [],
    placement: "game-shell-scalinata-mobile-footer-banner",
    testId: "scalinata-mobile-footer-banner",
  }), [endSectionAnchor]);

  const snackAlertRef = useRef();
  const hasShownInstructionsRef = useRef(false);
  
  const handleDefinitionRef = useRef();
  const handleClueRef = useRef();
  const handleMagicRef = useRef();
  const handleRevealThemeRef = useRef();
  const aiutiniModalSignatureRef = useRef("");

  useEffect(() => {
    if (gameOver && !showEndContent) {
      if (!won) {
        const lastPlayedLevel = Math.max(...Object.keys(allGuesses).map(Number), 0);
        const lastPlayedNumberOfLetters = wordLengths[lastPlayedLevel] || wordLengths[wordLengths.length - 1] || 0;
        const lastWordDelay = 0.5 + (0.5 * (8 - lastPlayedNumberOfLetters));
        const totalDelay = (lastWordDelay * 1000) + 1500;
        
        const timer = setTimeout(() => {
          setShowEndContent(true);
        }, totalDelay);
        return () => clearTimeout(timer);
      } else {
        setShowEndContent(true);
      }
    }
  }, [gameOver, won, showEndContent, allGuesses, wordLengths]);

  useEffect(() => {
    if (isPreview || isLoading) return;
    if (!hasShownInstructionsRef.current && !hasGameHistory(gameConfig.id)) {
      hasShownInstructionsRef.current = true;
      showModal({
        title: "Istruzioni",
        content: <Instructions wordLengths={wordLengths} maxGuesses={maxGuesses} />
      });
      if (shouldTrack) {
        logGameEvent('instructions_viewed', gameConfig.id, effectiveDate, { first_time: true });
      }
    }
  }, [isPreview, isLoading, gameConfig.id, effectiveDate, showModal, shouldTrack, wordLengths, maxGuesses]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isSubmitPending) return;
      const { key, ctrlKey, metaKey, altKey } = event;
      if (ctrlKey || metaKey || altKey) return;
      if (/^[a-zA-Z]$/.test(key)) {
        handleLetterClick(key.toUpperCase());
      } else if (key === 'Backspace') {
        handleDelete();
      } else if (key === 'Enter') {
        event.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentGuess, isSubmitPending]);

  const handleLetterClick = useCallback((letter) => {
    if (currentGuess.length < currentWordLength) {
      setCurrentGuess((prev) => prev + letter);
    }
  }, [currentGuess.length, currentWordLength]);

  const handleDelete = useCallback(() => {
    setCurrentGuess((prev) => prev.slice(0, -1));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (remainingGuesses <= 0 || gameOver) {
      snackAlertRef.current?.showSnackAlert("Non hai più tentativi", "error");
      return;
    }

    if (currentGuess.length !== currentWordLength) {
      snackAlertRef.current?.showSnackAlert(`La parola deve essere di ${currentWordLength} lettere`, "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await submitWord(currentLevel, currentGuess);

      if (!response.success) {
        snackAlertRef.current?.showSnackAlert(response.message || "Errore durante l'invio", "error");
        return;
      }

      const result = response.result;

      if (!result.valid) {
        if (shouldTrack) {
          logGameEvent('guess_invalid', gameConfig.id, effectiveDate, { word: currentGuess });
        }
        snackAlertRef.current?.showSnackAlert("Parola non valida", "error");
        setCurrentGuess("");
        return;
      }

      if (result.level_complete) {
        if (shouldTrack) {
          logGameEvent('guess_correct', gameConfig.id, effectiveDate);
          logGameEvent('level_completed', gameConfig.id, effectiveDate, {
            game_progress: (currentLevel + 1) / levels.length
          });
        }
        snackAlertRef.current?.showSnackAlert("Complimenti!", "success");
      } else if (shouldTrack) {
        logGameEvent('guess_incorrect', gameConfig.id, effectiveDate);
      }
      
      if ((result.game_lost || result.game_won) && result.answers) {
        setRevealedAnswers(result.answers.map(a => a?.toUpperCase() || null));
      }
      
      setCurrentGuess("");
    } catch (error) {
      console.error(error);
      snackAlertRef.current?.showSnackAlert("Errore durante l'invio della parola", "error");
    } finally {
      setIsSubmitting(false);
    }
  }, [remainingGuesses, gameOver, currentGuess, currentLevel, currentWordLength, submitWord, gameConfig.id, effectiveDate, shouldTrack, levels.length]);

  const redirectToAuth = useCallback((allowanceOverride = null) => {
    if (isPreview) return;
    const authUrl = buildTrackedAuthRedirectUrl({
      cta_kind: (hasEverAuthenticated || hintAllowance?.eligible === false) ? 'sign_in' : 'sign_up',
      source_surface: 'game_hints',
      gate_reason: 'hints_requires_auth',
      next_path: `${location.pathname}${location.search || ''}${location.hash || ''}`,
      message: getHintAuthMessage(allowanceOverride?.game_slug === gameConfig.id ? allowanceOverride : hintAllowance),
      is_returning_device: hasEverAuthenticated,
      game_slug: gameConfig?.id || null,
      track_gate_viewed: true,
      trigger: 'hint_click_redirect',
    });
    navigate(authUrl);
  }, [gameConfig?.id, hasEverAuthenticated, location.pathname, location.search, navigate, isPreview, hintAllowance, location.hash]);

  const hintCosts = useMemo(() => ({
    definition: getHintCost('scalinata', 'definition'),
    clue: getHintCost('scalinata', 'clue'),
    magic: getHintCost('scalinata', 'magic'),
    reveal_theme: getHintCost('scalinata', 'reveal_theme')
  }), [getHintCost]);

  const hintAffordability = useMemo(() => ({
    definition: canAffordHintCost({ cost: hintCosts.definition, balance, isPreview, isFree: hasFreeHints }),
    clue: canAffordHintCost({ cost: hintCosts.clue, balance, isPreview, isFree: hasFreeHints }),
    magic: canAffordHintCost({ cost: hintCosts.magic, balance, isPreview, isFree: hasFreeHints }),
    reveal_theme: canAffordHintCost({ cost: hintCosts.reveal_theme, balance, isPreview, isFree: hasFreeHints }),
  }), [balance, hintCosts, isPreview, hasFreeHints]);

  const handleDefinition = useCallback(async () => {
    if (gameOver) {
      return;
    }
    if (!isSignedIn && !isPreview && !hasFreeHints) {
      redirectToAuth();
      return;
    }
    if (!canAffordHintCost({ cost: hintCosts.definition, balance, isPreview, isFree: hasFreeHints })) {
      if (shouldTrack) {
        trackGameHintInsufficientBalance({
          gameSlug: gameConfig.id,
          gameDate: effectiveDate,
          hintType: 'hint_definition',
          source: 'client_precheck',
          balanceBefore: balance,
          hintCost: hintCosts.definition,
          modalName: 'aiutini',
        });
      }
      setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
      return;
    }
    
    setAiutiniError("");
    setLoadingHint('definition');
    try {
      const response = await requestDefinition(currentLevel, redirectToAuth);
      
      if (!response.success) {
        setAiutiniError(getScalinataHintErrorMessage(response));
        return;
      }
      
      closeModal();
    } catch (err) {
      console.error(err);
      setAiutiniError("Errore durante la richiesta");
    } finally {
      setLoadingHint(null);
    }
  }, [balance, closeModal, currentLevel, effectiveDate, gameConfig.id, gameOver, hintCosts.definition, isPreview, isSignedIn, hasFreeHints, redirectToAuth, requestDefinition, shouldTrack, trackGameHintInsufficientBalance]);
  handleDefinitionRef.current = handleDefinition;

  const handleClue = useCallback(async () => {
    if (gameOver) {
      return;
    }
    if (!isSignedIn && !isPreview && !hasFreeHints) {
      redirectToAuth();
      return;
    }
    if (!canAffordHintCost({ cost: hintCosts.clue, balance, isPreview, isFree: hasFreeHints })) {
      if (shouldTrack) {
        trackGameHintInsufficientBalance({
          gameSlug: gameConfig.id,
          gameDate: effectiveDate,
          hintType: 'hint_clue',
          source: 'client_precheck',
          balanceBefore: balance,
          hintCost: hintCosts.clue,
          modalName: 'aiutini',
        });
      }
      setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
      return;
    }

    setAiutiniError("");
    setLoadingHint('clue');
    try {
      const response = await requestClue(currentLevel, redirectToAuth);

      if (!response.success) {
        setAiutiniError(getScalinataHintErrorMessage(response));
        return;
      }

      closeModal();
    } catch (err) {
      console.error(err);
      setAiutiniError("Errore durante la richiesta");
    } finally {
      setLoadingHint(null);
    }
  }, [balance, closeModal, currentLevel, effectiveDate, gameConfig.id, gameOver, hintCosts.clue, isPreview, isSignedIn, hasFreeHints, redirectToAuth, requestClue, shouldTrack, trackGameHintInsufficientBalance]);
  handleClueRef.current = handleClue;

  const handleMagic = useCallback(async () => {
    if (gameOver) {
      return;
    }
    if (!isSignedIn && !isPreview && !hasFreeHints) {
      redirectToAuth();
      return;
    }
    if (!canAffordHintCost({ cost: hintCosts.magic, balance, isPreview, isFree: hasFreeHints })) {
      if (shouldTrack) {
        trackGameHintInsufficientBalance({
          gameSlug: gameConfig.id,
          gameDate: effectiveDate,
          hintType: 'hint_magic',
          source: 'client_precheck',
          balanceBefore: balance,
          hintCost: hintCosts.magic,
          modalName: 'aiutini',
        });
      }
      setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
      return;
    }
    
    setAiutiniError("");
    setLoadingHint('magic');
    try {
      const response = await requestMagic(currentLevel, redirectToAuth);
      
      if (!response.success) {
        setAiutiniError(getScalinataHintErrorMessage(response));
        return;
      }
      
      closeModal();
    } catch (err) {
      console.error(err);
      setAiutiniError("Errore durante la richiesta");
    } finally {
      setLoadingHint(null);
    }
  }, [balance, closeModal, currentLevel, effectiveDate, gameConfig.id, gameOver, hintCosts.magic, isPreview, isSignedIn, hasFreeHints, redirectToAuth, requestMagic, shouldTrack, trackGameHintInsufficientBalance]);
  handleMagicRef.current = handleMagic;

  const handleRevealTheme = useCallback(async () => {
    if (gameOver) {
      return;
    }
    if (!isSignedIn && !isPreview && !hasFreeHints) {
      redirectToAuth();
      return;
    }
    if (!canAffordHintCost({ cost: hintCosts.reveal_theme, balance, isPreview, isFree: hasFreeHints })) {
      if (shouldTrack) {
        trackGameHintInsufficientBalance({
          gameSlug: gameConfig.id,
          gameDate: effectiveDate,
          hintType: 'hint_reveal_theme',
          source: 'client_precheck',
          balanceBefore: balance,
          hintCost: hintCosts.reveal_theme,
          modalName: 'aiutini',
        });
      }
      setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
      return;
    }
    
    setAiutiniError("");
    setLoadingHint('reveal_theme');
    try {
      const response = await requestRevealTheme(redirectToAuth);
      
      if (!response.success) {
        setAiutiniError(getScalinataHintErrorMessage(response));
        return;
      }
      
      snackAlertRef.current?.showSnackAlert("Aiutino usato!", "success", 2000);
      closeModal();
    } catch (err) {
      console.error(err);
      setAiutiniError("Errore durante la richiesta");
    } finally {
      setLoadingHint(null);
    }
  }, [balance, closeModal, effectiveDate, gameConfig.id, gameOver, hintCosts.reveal_theme, isPreview, isSignedIn, hasFreeHints, redirectToAuth, requestRevealTheme, shouldTrack, trackGameHintInsufficientBalance]);
  handleRevealThemeRef.current = handleRevealTheme;

  useEffect(() => {
    if (!isAiutiniModalOpen) {
      aiutiniModalSignatureRef.current = "";
      setAiutiniError("");
      return;
    }

    const nextSignature = JSON.stringify({
      loadingHint: loadingHint || "",
      aiutiniError,
      revealThemeUsed: !!revealThemeUsed,
      disabled: !!gameOver,
      definitionCost: hintCosts.definition ?? null,
      clueCost: hintCosts.clue ?? null,
      magicCost: hintCosts.magic ?? null,
      revealThemeCost: hintCosts.reveal_theme ?? null,
      definitionAffordable: !!hintAffordability.definition,
      clueAffordable: !!hintAffordability.clue,
      magicAffordable: !!hintAffordability.magic,
      revealThemeAffordable: !!hintAffordability.reveal_theme,
      isSignedIn,
      hintAllowance,
      hasFreeHints,
      hasEverAuthenticated,
      isPreview,
    });

    if (aiutiniModalSignatureRef.current === nextSignature) {
      return;
    }
    aiutiniModalSignatureRef.current = nextSignature;

    refreshAiutiniModal(
      <Aiutini
        revealThemeUsed={revealThemeUsed}
        onDefinition={() => handleDefinitionRef.current?.()}
        onClue={() => handleClueRef.current?.()}
        onMagic={() => handleMagicRef.current?.()}
        onRevealTheme={() => handleRevealThemeRef.current?.()}
        disabled={gameOver}
        hintCosts={hintCosts}
        hintAffordability={hintAffordability}
        loadingHint={loadingHint}
        errorMessage={aiutiniError}
        isSignedIn={isSignedIn}
        hasFreeHints={hasFreeHints}
        onAuthRedirect={redirectToAuth}
        hasEverAuthenticated={hasEverAuthenticated || hintAllowance?.eligible === false}
        isPreview={isPreview}
        balance={balance}
      />,
      "Aiutini",
    );
  }, [aiutiniError, balance, gameOver, hasEverAuthenticated, hintAffordability, hintCosts, isAiutiniModalOpen, isPreview, isSignedIn, hasFreeHints, loadingHint, redirectToAuth, refreshAiutiniModal, revealThemeUsed, hintAllowance]);

  const remainingGuessesAdvice = (remainingGuesses > 1)
    ? `${remainingGuesses} tentativi rimanenti`
    : (remainingGuesses === 1) ? "L'ultimo tentativo!" : "Il gioco è finito";
  
  const levelsToShow = gameOver
    ? levels
    : levels.filter((level) => level <= currentLevel);
  
  const lastPlayedNumberOfLetters = Object.keys(allGuesses).length
    ? (wordLengths[Math.max(...Object.keys(allGuesses).map(Number))] || wordLengths[0] || 0)
    : (wordLengths[0] || 0);
  
  const winner = won;

  const handleClosePreview = useCallback(() => {
    window.close();
  }, []);

  const headerActions = [
    {
      icon: "fa fa-question-circle",
      title: "Istruzioni",
      onClick: () => {
        if (shouldTrack) {
          logGameEvent('instructions_viewed', gameConfig.id, effectiveDate, { first_time: false });
        }
        showModal({
          title: "Istruzioni",
          content: <Instructions wordLengths={wordLengths} maxGuesses={maxGuesses} />
        });
      }
    },
    {
      icon: "fa fa-medkit",
      title: "Aiutini",
      onClick: () => {
        if (shouldTrack) {
          logGameEvent('help_viewed', gameConfig.id, effectiveDate);
        }
        setAiutiniError("");
        showAiutiniModal(
          <Aiutini
            revealThemeUsed={revealThemeUsed}
            onDefinition={() => handleDefinitionRef.current?.()}
            onClue={() => handleClueRef.current?.()}
            onMagic={() => handleMagicRef.current?.()}
            onRevealTheme={() => handleRevealThemeRef.current?.()}
            disabled={gameOver}
            hintCosts={hintCosts}
            hintAffordability={hintAffordability}
            loadingHint={loadingHint}
            errorMessage=""
            isSignedIn={isSignedIn}
            hasFreeHints={hasFreeHints}
            onAuthRedirect={redirectToAuth}
            hasEverAuthenticated={hasEverAuthenticated || hintAllowance?.eligible === false}
            isPreview={isPreview}
            balance={balance}
          />,
          "Aiutini",
        );
      }
    },
    {
      icon: showAllGuesses ? "fa fa-eye-slash" : "fa fa-eye",
      title: showAllGuesses ? "Nascondi tentativi intermedi" : "Mostra tutti i tentativi",
      onClick: () => {
        setShowAllGuesses(prev => !prev);
      }
    },
  ];

  if (isLoading) {
    return (
      <GameShell
        gameConfig={gameConfig}
        actions={headerActions}
        sponsorship={sponsorship}
        isModalOpen={isModalOpen}
        onModalClose={closeModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        showNavigation={showNavigation}
        showGameHeader={showGameHeader}
      >
        <ScalinataSkeleton />
      </GameShell>
    );
  }

  if (sessionError && !sessionState) {
    return (
      <GameShell
        gameConfig={gameConfig}
        actions={headerActions}
        sponsorship={sponsorship}
        isModalOpen={isModalOpen}
        onModalClose={closeModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        showNavigation={showNavigation}
        showGameHeader={showGameHeader}
      >
        <div className={styles.scalinataContainer}>
          <p className={globalStyles.error}>Errore nel caricamento del gioco. Riprova più tardi.</p>
        </div>
      </GameShell>
    );
  }

  const aiSelectionEnabled = gameOver && !isPreview;
  const isAIOnboardingReady = gameOver && showEndContent && !isPreview;

  return (
    <GameShell
      gameConfig={gameConfig}
      actions={headerActions}
      sponsorship={sponsorship}
      isModalOpen={isModalOpen}
      onModalClose={closeModal}
      modalTitle={modalTitle}
      modalContent={modalContent}
      key={sessionState?.session_id || effectiveDate}
      resultsPresentationReady={showEndContent}
      showCelebration={justWon}
      isCompleted={gameOver}
      showNavigation={showNavigation}
      showGameHeader={showGameHeader}
      mobileGameplayAd={mobileGameplayAd}
    >
      <SnackAlert ref={snackAlertRef} />
      <div
        className={styles.scalinataContainer}
        style={{ '--scalinata-footer-ad-height': `${footerAdHeight}px` }}
      >
        {!gameOver && (
          <p className={gameStyles.greeting}>
            {getGameMessage(gameConfig.id, 'not_started')}
          </p>
        )}

        <AIHelperTrigger
          puzzleInstanceId={puzzleInstanceId}
          gameSlug="scalinata"
          enabled={aiSelectionEnabled}
          onboardingReady={isAIOnboardingReady}
          coachmarkSelector='[data-ai-component="answer_word"]'
        >
          {levelsToShow.map((level) => {
            const isActive = level === currentLevel;
            const guesses = allGuesses[level] || [];
            const isDone = guesses.length > 0
              ? guesses[guesses.length - 1].feedback.every(f => f.state === 'correct')
              : false;
            const showAnswer = !isDone && answers[level] && gameOver;
            const numberOfLetters = wordLengths[level] || 0;
            const animationDelay = guesses.length > 0
              ? 0.5
              : 0.5 + (numberOfLetters - lastPlayedNumberOfLetters);
            return (
              <ScalinataLevel
                key={level}
                level={level}
                isActive={isActive && !isDone}
                numberOfLetters={numberOfLetters}
                guesses={guesses}
                currentGuess={currentGuess}
                gameOver={gameOver}
                answer={answers[level]}
                showAnswer={showAnswer}
                animationDelay={animationDelay}
                magicLetters={magicLetters}
                isSubmitting={isSubmitPending}
                showAllGuesses={showAllGuesses}
              />
            );
          })}

          {!gameOver && (revealedTheme || definition || clue || magicLetters) && (
            <div className={styles.hintDisplay}>
              {revealedTheme && (
                <div className={styles.hintItem}>
                  <FaIcon className="fa-solid fa-tag"  />
                  <span data-ai-component="theme"><strong>{revealedTheme}</strong></span>
                </div>
              )}
              {definition && (
                <div className={styles.hintItem}>
                  <FaIcon className="fa-solid fa-book"  />
                  <span data-ai-component="definition" data-ai-word={answers[currentLevel]}>{definition}</span>
                </div>
              )}
              {clue && (
                <div className={styles.hintItem}>
                  <FaIcon className="fa-solid fa-lightbulb"  />
                  <span data-ai-component="clue" data-ai-word={answers[currentLevel]}>{clue}</span>
                </div>
              )}
              {magicLetters && (
                <div className={styles.hintItem}>
                  <FaIcon className="fa-solid fa-wand-magic-sparkles"  />
                  <span>Tre lettere sono state rivelate a caso per il prossimo tentativo!</span>
                </div>
              )}
            </div>
          )}

          {gameOver && showEndContent && (
            <>
              {theme && (
                <div className={styles.hintDisplay}>
                  <div className={styles.hintItem}>
                    <FaIcon className="fa-solid fa-tag"  />
                    <span data-ai-component="theme"><strong>{theme}</strong></span>
                  </div>
                </div>
              )}
            </>
          )}
        </AIHelperTrigger>

        {gameOver && showEndContent && (
          <>
            {isPreview && !previewSponsorship ? (
              <div ref={setEndSectionAnchor} className={styles.previewEndSection}>
                <Button primary onClick={handleClosePreview}>
                  {previewEndLabel || "Chiudi anteprima"}
                </Button>
              </div>
            ) : (
              <GameEndSection
                ref={setEndSectionAnchor}
                won={winner}
                justWon={justWon}
                gameConfig={gameConfig}
                sessionState={sessionState}
                puzzleConfig={puzzleConfig}
                justCompleted={justWon || justLost}
                date={effectiveDate}
                shouldTrack={shouldTrack}
                sponsorship={sponsorship}
                previewMode={isPreview}
                message={winner ? 'Hai trovato tutte le parole! 🚀' : 'Peccato! I tentativi sono esauriti. 💪'}
                snackAlertRef={snackAlertRef}
              />
            )}
          </>
        )}
      </div>
      
      {!gameOver && (
        <div className={styles.bottomContainer}>
          <div className={styles.remaining}>•&nbsp;&nbsp;{remainingGuessesAdvice}&nbsp;&nbsp;•</div>
          <Keyboard
            keyboardState={keyboardState}
            handleSubmit={handleSubmit}
            handleDelete={handleDelete}
            handleLetterClick={handleLetterClick}
            isSubmitting={isSubmitPending}
          />
          {footerBannerSize ? (
            <div className={styles.keyboardAdSlot}>
              <AdBanner
                placement="game-shell-scalinata-mobile-footer-banner"
                size={footerBannerSize}
                minViewportWidth={0}
                testId="scalinata-mobile-footer-banner"
              />
            </div>
          ) : null}
        </div>
      )}
    </GameShell>
  );
};

const MiniScalinataDemo = () => {
  const demoGuesses = [
    { guess: 'PESCA', feedback: ['incorrect', 'position', 'incorrect', 'correct', 'incorrect'] },
    { guess: 'SCENA', feedback: ['correct', 'correct', 'position', 'incorrect', 'correct'] },
  ];
  const [phase, setPhase] = useState(0);
  const [guessIndex, setGuessIndex] = useState(0);

  useEffect(() => {
    let timer;
    if (phase < 5) {
      timer = setTimeout(() => setPhase(phase + 1), 220);
    } else if (phase === 5) {
      timer = setTimeout(() => setPhase(6), 400);
    } else if (phase === 6) {
      timer = setTimeout(() => setPhase(7), 900);
    } else if (phase === 7) {
      if (guessIndex < demoGuesses.length - 1) {
        timer = setTimeout(() => { setGuessIndex(guessIndex + 1); setPhase(0); }, 400);
      } else {
        timer = setTimeout(() => { setGuessIndex(0); setPhase(0); }, 900);
      }
    }
    return () => clearTimeout(timer);
  }, [phase, guessIndex]);

  const current = demoGuesses[guessIndex];

  return (
    <div className={styles.miniScalinataDemo}>
      <div className={styles.miniScalinataRow}>
        {Array.from({ length: 5 }).map((_, j) => {
          let letter = '';
          let boxClass = styles.miniScalinataBox;
          if (phase > j) {
            letter = current.guess[j];
          }
          if (phase >= 6) {
            const feedbackClass = styles[current.feedback[j]];
            if (feedbackClass) boxClass += ' ' + feedbackClass;
          }
          return (
            <span className={boxClass} key={j}>{letter}</span>
          );
        })}
      </div>
    </div>
  );
};

const Instructions = ({ wordLengths = [4, 5, 6, 7, 8], maxGuesses = 20 }) => {
  const firstWordLength = wordLengths[0] || 4;
  const lastWordLength = wordLengths[wordLengths.length - 1] || firstWordLength;

  return (
    <div className={`${modalStyles.modalContent} ${modalStyles.instructions}`}>
      <p>Trova le {wordLengths.length} parole nascoste nei {maxGuesses} tentativi a disposizione!</p>
      <p>Scrivi una parola e premi il tasto "Invia" per controllare la tua idea. Ricorda che:</p>
      <ul>
        <li><strong>Lettera verde</strong> = la lettera c'è nella parola ed è nella posizione corretta;</li>
        <li><strong>Lettera gialla</strong> = la lettera c'è nella parola ma non è nella posizione corretta;</li>
        <li><strong>Lettera grigia</strong> = la lettera non c'è nella parola.</li>
      </ul>
      <MiniScalinataDemo />
      <p>Inoltre:</p>
      <ul>
        <li>La lunghezza delle parole aumenta: la prima parola ha {firstWordLength} lettere, l'ultima parola ha {lastWordLength} lettere;</li>
        <li>Tutte le parole hanno un tema in comune.</li>
        <li>Non è possibile usare nomi propri, abbreviazioni o parole con trattini.</li>
        <li>Devi usare solo parole presenti nella lingua italiana.</li>
      </ul>
      <p>Se resti bloccato, puoi usare uno degli aiutini <FaIcon className="fa fa-medkit"  /> a tua disposizione in alto a destra.</p>
    </div>
  );
};

const Aiutini = ({
  revealThemeUsed,
  onDefinition,
  onClue,
  onMagic,
  onRevealTheme,
  disabled = false,
  hintCosts = {},
  hintAffordability = {},
  loadingHint = null,
  errorMessage = "",
  isSignedIn = true,
  hasFreeHints = false,
  onAuthRedirect = null,
  hasEverAuthenticated = false,
  isPreview = false,
  balance = 0,
}) => {
  const renderButtonContent = ({ alreadyUsed = false } = {}) => {
    if (isPreview) {
      return 'Ricevi aiutino';
    }
    if (!isSignedIn && !hasFreeHints) {
      return hasEverAuthenticated ? 'Accedi' : 'Registrati';
    }
    if (alreadyUsed) {
      return 'Già usato';
    }
    return 'Usa aiutino';
  };
  const shouldShowCostChip = (cost) => !isPreview && (hasFreeHints || (Number.isFinite(Number(cost)) && Number(cost) > 0));
  const disabledHintCount = getDisabledHintCount({
    isSignedIn,
    isPreview,
    hintAffordability,
    excludedHintKeys: revealThemeUsed ? ['reveal_theme'] : [],
  });
  const shouldShowInsufficientBalanceState = isSignedIn && (
    errorMessage === INSUFFICIENT_HINT_BALANCE_MESSAGE
    || disabledHintCount > 0
  );
  const shouldShowGenericError = Boolean(errorMessage) && !shouldShowInsufficientBalanceState;

  return (
    <div className={modalStyles.modalContent}>
      {shouldShowInsufficientBalanceState && (
        <div className={styles.aiutiniInlineError} role="alert">
          <FaIcon className="fa-solid fa-circle-exclamation" />
          <div className={styles.aiutiniInlineErrorContent}>
            <div>
              {getDisabledHintsMessage({
                balance,
                disabledHintCount: Math.max(disabledHintCount, 1),
              })}
            </div>
            <div className={styles.aiutiniInlineErrorCard}>
              <RewardedGettonciniCard
                sourceSurface="aiutini_modal"
                gameSlug="scalinata"
                compact
              />
            </div>
          </div>
        </div>
      )}
      {shouldShowGenericError && (
        <div className={styles.aiutiniInlineError} role="alert">
          <FaIcon className="fa-solid fa-circle-exclamation" />
          <span>
            {errorMessage}
          </span>
        </div>
      )}
      {(!isSignedIn || !shouldShowInsufficientBalanceState) && (
        <p className={styles.aiutiniPreamble}>
          {!isSignedIn && !isPreview && !hasFreeHints ? (
            <>
              Per usare gli aiutini hai bisogno di gettoncini, che puoi guadagnare con un account gratuito.{' '}
              <span className={styles.authLink} onClick={onAuthRedirect}>
                {hasEverAuthenticated ? 'Accedi' : 'Registrati'} <FaIcon className="fa-solid fa-arrow-right"  />
              </span>
            </>
          ) : 'Se sei bloccato, puoi usare uno di questi aiutini.'}
        </p>
      )}
      <div className={styles.aiutinoRow}>
        <div className={styles.aiutinoHeaderRow}>
          <div className={styles.aiutinoHeaderTitle}>
            <span className={styles.aiutinoIcon}><FaIcon className="fa-solid fa-book"  /></span>
            <span className={styles.aiutinoTitle}>Definizione</span>
            <span className={styles.reusableBadge}><FaIcon className="fa-solid fa-rotate"  /></span>
          </div>
          <div className={styles.aiutinoActionWrap}>
            <Button
              secondary
              className={[
                isSignedIn && !hintAffordability.definition ? styles.aiutinoUnavailable : '',
              ].filter(Boolean).join(' ')}
              onClick={onDefinition}
              disabled={disabled || loadingHint !== null || (isSignedIn && !hintAffordability.definition)}
              loading={loadingHint === 'definition'}
              aria-disabled={disabled || (isSignedIn && !hintAffordability.definition)}
            >
              {renderButtonContent()}
            </Button>
            {shouldShowCostChip(hintCosts.definition) && (
              <GettonciniCostChip free={hasFreeHints} amount={hintCosts.definition} compact className={styles.aiutinoCostChip} />
            )}
          </div>
        </div>
        <span className={styles.aiutinoDesc}>Mostra una definizione della parola da indovinare.</span>
      </div>
      <div className={styles.aiutinoRow}>
        <div className={styles.aiutinoHeaderRow}>
          <div className={styles.aiutinoHeaderTitle}>
            <span className={styles.aiutinoIcon}><FaIcon className="fa-solid fa-lightbulb"  /></span>
            <span className={styles.aiutinoTitle}>Indovinello</span>
            <span className={styles.reusableBadge}><FaIcon className="fa-solid fa-rotate"  /></span>
          </div>
          <div className={styles.aiutinoActionWrap}>
            <Button
              secondary
              className={[
                isSignedIn && !hintAffordability.clue ? styles.aiutinoUnavailable : '',
              ].filter(Boolean).join(' ')}
              onClick={onClue}
              disabled={disabled || loadingHint !== null || (isSignedIn && !hintAffordability.clue)}
              loading={loadingHint === 'clue'}
              aria-disabled={disabled || (isSignedIn && !hintAffordability.clue)}
            >
              {renderButtonContent()}
            </Button>
            {shouldShowCostChip(hintCosts.clue) && (
              <GettonciniCostChip free={hasFreeHints} amount={hintCosts.clue} compact className={styles.aiutinoCostChip} />
            )}
          </div>
        </div>
        <span className={styles.aiutinoDesc}>Mostra un indovinello legato alla parola nascosta.</span>
      </div>
      <div className={styles.aiutinoRow}>
        <div className={styles.aiutinoHeaderRow}>
          <div className={styles.aiutinoHeaderTitle}>
            <span className={styles.aiutinoIcon}><FaIcon className="fa-solid fa-wand-magic-sparkles"  /></span>
            <span className={styles.aiutinoTitle}>Magia</span>
            <span className={styles.reusableBadge}><FaIcon className="fa-solid fa-rotate"  /></span>
          </div>
          <div className={styles.aiutinoActionWrap}>
            <Button
              secondary
              className={[
                isSignedIn && !hintAffordability.magic ? styles.aiutinoUnavailable : '',
              ].filter(Boolean).join(' ')}
              onClick={onMagic}
              disabled={disabled || loadingHint !== null || (isSignedIn && !hintAffordability.magic)}
              loading={loadingHint === 'magic'}
              aria-disabled={disabled || (isSignedIn && !hintAffordability.magic)}
            >
              {renderButtonContent()}
            </Button>
            {shouldShowCostChip(hintCosts.magic) && (
              <GettonciniCostChip free={hasFreeHints} amount={hintCosts.magic} compact className={styles.aiutinoCostChip} />
            )}
          </div>
        </div>
        <span className={styles.aiutinoDesc}>Rivela tre lettere corrette nella posizione giusta per il tentativo successivo.</span>
      </div>
      <div className={styles.aiutinoRow}>
        <div className={styles.aiutinoHeaderRow}>
          <div className={styles.aiutinoHeaderTitle}>
            <span className={styles.aiutinoIcon}><FaIcon className="fa-solid fa-tag"  /></span>
            <span className={styles.aiutinoTitle}>Rivela tema</span>
          </div>
          <div className={styles.aiutinoActionWrap}>
            <Button
              secondary
              className={[
                isSignedIn && !revealThemeUsed && !hintAffordability.reveal_theme ? styles.aiutinoUnavailable : '',
              ].filter(Boolean).join(' ')}
              onClick={onRevealTheme}
              disabled={disabled || revealThemeUsed || loadingHint !== null || (isSignedIn && !revealThemeUsed && !hintAffordability.reveal_theme)}
              loading={loadingHint === 'reveal_theme'}
              aria-disabled={isSignedIn && !revealThemeUsed && !hintAffordability.reveal_theme}
            >
              {renderButtonContent({ alreadyUsed: revealThemeUsed })}
            </Button>
            {shouldShowCostChip(hintCosts.reveal_theme) && (
              <GettonciniCostChip free={hasFreeHints} amount={hintCosts.reveal_theme} compact className={styles.aiutinoCostChip} />
            )}
          </div>
        </div>
        <span className={styles.aiutinoDesc}>Mostra il tema nascosto che collega tutte le parole.</span>
      </div>
      <div className={styles.reusableHint}>
        <FaIcon className="fa-solid fa-rotate"  />
        <span>Possono essere riutilizzati più volte.</span>
      </div>
    </div>
  );
};

export default ScalinataGame;
